import { useCallback, useEffect, useRef, useState } from 'react';
import { MOBILE_MQ } from './helpers';

const SWIPE_OPEN_PX = 24;
const SWIPE_CLOSE_PX = 32;
/** After the transcript is on screen, wait before sliding the profile open on load. */
const OPEN_AFTER_MESSAGES_MS = 3000;
/** After a new assistant reply is on screen, wait before sliding the profile open. */
const OPEN_AFTER_REPLY_MS = 3000;

/**
 * Profile drawer shared by the copilot chat and Finance Q&A.
 * On a wide screen, messages show first, then the panel opens after 3s.
 * Each new assistant reply waits 3s, then opens it again.
 * `suppressAutoOpen` keeps it closed (first-run location prompt) without counting as a user dismiss.
 */
export function useProfileDrawer({
  autoReveal = false,
  initialOpen = false,
  delayInitialOpen = false,
  pinnedClosed = false,
  suppressAutoOpen = false,
  responseId = null,
  messagesVisible = false,
  active = true,
  onToggle,
  onAutoReveal,
}) {
  const [open, setOpen] = useState(initialOpen && !delayInitialOpen && !suppressAutoOpen);
  const openRef = useRef(open);
  openRef.current = open;
  const baselinedRef = useRef(false);
  const seenResponseIdRef = useRef(null);
  const initialOpenDoneRef = useRef(false);
  const dismissedRef = useRef(false);
  const touchStartXRef = useRef(0);
  const pinnedClosedRef = useRef(pinnedClosed);
  pinnedClosedRef.current = pinnedClosed;
  const suppressAutoOpenRef = useRef(suppressAutoOpen);
  suppressAutoOpenRef.current = suppressAutoOpen;
  const onToggleRef = useRef(onToggle);
  onToggleRef.current = onToggle;
  const onAutoRevealRef = useRef(onAutoReveal);
  onAutoRevealRef.current = onAutoReveal;

  const close = useCallback(() => {
    dismissedRef.current = true;
    openRef.current = false;
    setOpen(false);
  }, []);

  const toggle = useCallback(() => {
    if (pinnedClosedRef.current) return;
    const next = !openRef.current;
    if (!next) dismissedRef.current = true;
    openRef.current = next;
    setOpen(next);
    onToggleRef.current?.(next);
  }, []);

  useEffect(() => {
    if (!pinnedClosed) return;
    openRef.current = false;
    setOpen(false);
  }, [pinnedClosed]);

  useEffect(() => {
    if (!suppressAutoOpen) return;
    if (!dismissedRef.current) initialOpenDoneRef.current = false;
    openRef.current = false;
    setOpen(false);
  }, [suppressAutoOpen]);

  useEffect(() => {
    if (delayInitialOpen || suppressAutoOpen || !initialOpen || initialOpenDoneRef.current) return;
    if (pinnedClosedRef.current || dismissedRef.current) return;
    initialOpenDoneRef.current = true;
    if (openRef.current) return;
    openRef.current = true;
    setOpen(true);
  }, [delayInitialOpen, suppressAutoOpen, initialOpen]);

  useEffect(() => {
    if (
      !delayInitialOpen ||
      !initialOpen ||
      !messagesVisible ||
      suppressAutoOpen ||
      initialOpenDoneRef.current
    ) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      if (dismissedRef.current || openRef.current) {
        initialOpenDoneRef.current = true;
        return;
      }
      if (pinnedClosedRef.current || suppressAutoOpenRef.current) return;
      initialOpenDoneRef.current = true;
      openRef.current = true;
      setOpen(true);
      const isMobile = typeof window !== 'undefined' && window.matchMedia(MOBILE_MQ).matches;
      onAutoRevealRef.current?.('initial_load', isMobile ? 'mobile' : 'desktop');
    }, OPEN_AFTER_MESSAGES_MS);
    return () => window.clearTimeout(timer);
  }, [delayInitialOpen, initialOpen, messagesVisible, suppressAutoOpen]);

  useEffect(() => {
    if (!autoReveal || !active || pinnedClosedRef.current || suppressAutoOpenRef.current) return undefined;
    if (!baselinedRef.current) {
      baselinedRef.current = true;
      seenResponseIdRef.current = responseId;
      return undefined;
    }
    if (responseId == null || responseId === seenResponseIdRef.current) return undefined;
    seenResponseIdRef.current = responseId;
    if (openRef.current) return undefined;

    const replyId = responseId;
    dismissedRef.current = false;
    const timer = window.setTimeout(() => {
      if (pinnedClosedRef.current || suppressAutoOpenRef.current || dismissedRef.current || openRef.current) return;
      if (seenResponseIdRef.current !== replyId) return;
      openRef.current = true;
      setOpen(true);
      const isMobile = typeof window !== 'undefined' && window.matchMedia(MOBILE_MQ).matches;
      onAutoRevealRef.current?.('api_response', isMobile ? 'mobile' : 'desktop');
    }, OPEN_AFTER_REPLY_MS);
    return () => window.clearTimeout(timer);
  }, [autoReveal, active, responseId, suppressAutoOpen]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, close]);

  const handleTouchStart = useCallback((event) => {
    touchStartXRef.current = event.touches?.[0]?.clientX ?? 0;
  }, []);

  const handleTouchEnd = useCallback((event) => {
    if (pinnedClosedRef.current) return;
    const endX = event.changedTouches?.[0]?.clientX ?? 0;
    if (endX - touchStartXRef.current <= -SWIPE_OPEN_PX) {
      openRef.current = true;
      setOpen(true);
    }
  }, []);

  const drawerTouchStart = useCallback((event) => {
    touchStartXRef.current = event.touches?.[0]?.clientX ?? 0;
  }, []);

  const drawerTouchEnd = useCallback((event) => {
    const endX = event.changedTouches?.[0]?.clientX ?? 0;
    if (endX - touchStartXRef.current >= SWIPE_CLOSE_PX) close();
  }, [close]);

  return {
    open,
    close,
    toggle,
    handleTouchStart,
    handleTouchEnd,
    drawerTouchStart,
    drawerTouchEnd,
  };
}
